import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ScreenerFilterWithId } from '../components/screener/StockScreener'

export interface ChatState {
    sessions: Record<string, ChatSessionData>
    activeSessionId: string | null
}

export interface FeedbackFilterRecord {
    id: string
    query: string
    filterItems: FilterItem[]
}

export interface FilterItem {
    query: string
    generatedFilters: ScreenerFilterWithId[]
    updatedFilters: ScreenerFilterWithId[]
}

export interface QueryResponse {
    query: string
    // response: ScreenerFilterWithId[]
    response: FilterItem[]
    recordId: string
}

export interface ChatSessionData {
    createdAt: string
    queries: QueryResponse[]
}

const initialState: ChatState = {
    sessions: {},
    activeSessionId: null
}

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        startSession(state, action: PayloadAction<{ id: string; session: ChatSessionData }>) {
            const { id, session } = action.payload
            state.sessions[id] = session
            state.activeSessionId = id
        },
        addQueryResponse(state, action: PayloadAction<{ sessionId: string; queryResponse: QueryResponse }>) {
            const { sessionId, queryResponse } = action.payload
            const session = state.sessions[sessionId]
            if (session) {
                session.queries.push(queryResponse)
            }
        },
        setActiveSession(state, action: PayloadAction<string>) {
            if (state.sessions[action.payload]) {
                state.activeSessionId = action.payload
            }
        },
        deleteSession(state, action: PayloadAction<string>) {
            delete state.sessions[action.payload]
            if (state.activeSessionId === action.payload) {
                state.activeSessionId = null
            }
        },
        updateFilterValueInSession: (
            state,
            action: PayloadAction<{
                sessionId: string
                recordId: string
                filter: ScreenerFilterWithId
            }>
        ) => {
            const { sessionId, recordId, filter } = action.payload
            const session = state.sessions[sessionId]
            if (!session) return

            for (const query of session.queries) {
                if (query.recordId !== recordId) continue
                query.response = query.response.map((item) => ({
                    ...item,
                    updatedFilters: item.updatedFilters.map((f) => (f.id === filter.id ? filter : f))
                }))
            }
        },
        removeFilterFromSession: (
            state,
            action: PayloadAction<{
                sessionId: string
                recordId: string
                filterId: string
            }>
        ) => {
            const { sessionId, recordId, filterId } = action.payload
            const session = state.sessions[sessionId]
            if (!session) return

            session.queries = session.queries
                .map((query) => {
                    if (query.recordId !== recordId) return query

                    const updatedResponse = query.response.map((item) => ({
                        ...item,
                        updatedFilters: item.updatedFilters.filter((filter) => filter.id !== filterId)
                    }))

                    const allFiltersRemoved = updatedResponse.every((item) => item.updatedFilters.length === 0)

                    if (allFiltersRemoved) {
                        return null
                    }

                    return {
                        ...query,
                        response: updatedResponse
                    }
                })
                .filter((query): query is QueryResponse => query !== null)
        },
        setActiveSessionId: (state, action: PayloadAction<string>) => {
            state.activeSessionId = action.payload
        },
        updateRecordInSession: (
            state,
            action: PayloadAction<{
                sessionId: string
                recordId: string
                updatedRecord: FeedbackFilterRecord
            }>
        ) => {
            const { sessionId, recordId, updatedRecord } = action.payload
            const session = state.sessions[sessionId]
            if (!session) return

            session.queries = session.queries.map((query) => {
                if (query.recordId !== recordId) return query

                return {
                    ...query,
                    filterItems: updatedRecord.filterItems
                }
            })
        }
    }
})

export const { startSession, addQueryResponse, setActiveSession, deleteSession, updateFilterValueInSession, removeFilterFromSession, setActiveSessionId, updateRecordInSession } = chatSlice.actions
const { reducer: screenerChatReducer } = chatSlice
export default screenerChatReducer